<template>
  <div class="kitchen-bg">
    <!-- 固定标题栏 -->
    <div class="fixed-header">
      <div class="kitchen-container">
        <div class="header">
          <div class="title-section">
            <div class="title">菜品分配</div>
            <div class="restaurant-selector">
              <select v-model="currentShop" class="restaurant-dropdown" disabled>
                <option value="" disabled>请选择餐厅</option>
                <option v-for="shop in shops" :key="shop.shopId" :value="String(shop.shopId)">{{
                  shop.shopName
                }}</option>
              </select>
              <span v-if="selectionMessage" class="text-sm text-gray-500">{{
                selectionMessage
              }}</span>
              <select
                v-model="currentKitchen"
                :disabled="selectionLoading || !kitchens.length"
                class="restaurant-dropdown"
              >
                <option value="" disabled>请选择厨房</option>
                <option value="all">全部厨房</option>
                <option v-for="kitchen in kitchens" :key="kitchen.id" :value="kitchen.id">
                  {{ kitchen.name }}
                </option>
              </select>
            </div>
            <div class="summary-info">
              <span class="summary-badge" :class="{ completed: totalDishes === 0 }">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <span>{{ totalDishes }}</span
                >道菜品，<span>{{ totalPortions }}</span
                >份
              </span>
            </div>
          </div>
          <div class="actions">
            <!-- 新增：统一导航菜单 -->
            <div class="nav-dropdown">
              <button class="nav-menu-btn" @click="toggleNavMenu">
                <i class="fas fa-bars"></i> 菜单
                <i class="fas fa-chevron-down"></i>
              </button>
              <div class="nav-menu" :class="{ show: showNavMenu }">
                <button class="nav-menu-item" @click="goToTableManage">
                  <i class="fas fa-table"></i> 桌位管理
                </button>
                <button class="nav-menu-item" @click="goToKitchenOrder">
                  <i class="fas fa-utensils"></i> 订单出餐
                </button>
                <button class="nav-menu-item" @click="goToOrderHistory">
                  <i class="fas fa-receipt"></i> 订单查询
                </button>
                <button class="nav-menu-item" @click="goToDishAssignment">
                  <i class="fas fa-tasks"></i> 菜品分配
                </button>
                <button class="nav-menu-item" @click="goToServingHistory">
                  <i class="fas fa-history"></i> 出餐历史
                </button>
                <button class="nav-menu-item" @click="goToFoodManage">
                  <i class="fas fa-utensils"></i> 菜品管理
                </button>
              </div>
            </div>
            <div class="filter-dropdown">
              <button class="filter-btn" @click="toggleFilterMenu">
                <i class="fas fa-filter mr-2"></i>筛选
                <i class="fas fa-chevron-down ml-2"></i>
              </button>
              <div class="filter-menu" :class="{ show: showFilterMenu }">
                <div class="filter-menu-section">
                  <div class="filter-menu-title">排序方式</div>
                  <button
                    class="filter-menu-item"
                    :class="{ active: currentSort === 'time' }"
                    @click="applyFilter('sort', 'time')"
                  >
                    <i class="fas fa-clock mr-2"></i>按时间排序
                  </button>
                  <button
                    class="filter-menu-item"
                    :class="{ active: currentSort === 'dish' }"
                    @click="applyFilter('sort', 'dish')"
                  >
                    <i class="fas fa-utensils mr-2"></i>按菜品排序
                  </button>
                  <button
                    class="filter-menu-item"
                    :class="{ active: currentSort === 'table' }"
                    @click="applyFilter('sort', 'table')"
                  >
                    <i class="fas fa-table mr-2"></i>按餐桌排序
                  </button>
                  <button
                    class="filter-menu-item"
                    :class="{ active: currentSort === 'type' }"
                    @click="applyFilter('sort', 'type')"
                  >
                    <i class="fas fa-tags mr-2"></i>按类型排序
                  </button>
                </div>
                <div class="filter-menu-section">
                  <div class="filter-menu-title">菜品状态</div>
                  <button
                    class="filter-menu-item"
                    :class="{ active: currentFilter === 'all' }"
                    @click="applyFilter('filter', 'all')"
                  >
                    <i class="fas fa-list mr-2"></i>显示全部
                  </button>
                  <button
                    class="filter-menu-item"
                    :class="{ active: currentFilter === 'urgent' }"
                    @click="applyFilter('filter', 'urgent')"
                  >
                    <i class="fas fa-exclamation-triangle mr-2"></i>仅显示加急
                  </button>
                </div>
              </div>
            </div>
            <button class="ios-button-secondary" @click="refreshData">
              <i class="fas fa-sync-alt mr-2"></i>刷新
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="kitchen-container content-container">
      <div class="dish-layout">
        <!-- 灶台区域 -->
        <div v-if="!isAllKitchensMode" class="stove-section">
          <div class="section-title">
            <i class="fas fa-fire"></i>
            灶台状态
            <span class="section-count"
              >({{ stoveItemCount }}道菜品，{{ stovePortionCount }}份)</span
            >
          </div>
          <div class="stove-grid">
            <div
              v-for="stove in stoves"
              :key="stove.id"
              class="stove-table-container"
              :class="[`stove-${stove.id}`, getStoveStatusClass(stove)]"
            >
              <div class="stove-table-header">
                <div class="stove-title">
                  <div class="stove-icon">
                    <i class="fas fa-fire"></i>
                  </div>
                  <div class="stove-name">{{ stove.name }}</div>
                </div>
                <div class="stove-status-badge">
                  {{ getStoveStatusText(stove) }}
                  {{ stove.dishes.length }} 道菜
                </div>
              </div>
              <table class="stove-table">
                <thead>
                  <tr>
                    <th>菜品</th>
                    <th>桌位</th>
                    <th>份数</th>
                    <th>状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody class="stove-table-body">
                  <tr v-if="stove.dishes.length === 0" class="empty-stove-row">
                    <td colspan="5" class="empty-stove-row">
                      <i class="fas fa-utensils"></i>
                      <div>暂无菜品，点击右侧分配菜品</div>
                    </td>
                  </tr>
                  <tr v-for="(dish, index) in stove.dishes" :key="index" :class="dish.status">
                    <td style="text-align: left">
                      <div class="dish-name-spec-wrap">
                        <div class="dish-name-main">{{ dish.name }}</div>
                        <div
                          v-if="dish.foodSpecName && dish.foodSpecName !== '标准'"
                          class="dish-spec-plain"
                          >{{ dish.foodSpecName }}</div
                        >
                      </div>
                    </td>
                    <td>{{
                      dish.restaurantName
                        ? dish.restaurantName + '【' + dish.tableNumber + '】'
                        : dish.tableNumber
                    }}</td>
                    <td
                      ><span class="quantity-badge">{{ dish.quantity }}</span></td
                    >
                    <td>
                      <span :style="{ color: getDishStatusColor(dish.status), fontWeight: '600' }">
                        {{ getDishStatusText(dish.status) }}
                      </span>
                    </td>
                    <td>
                      <button
                        v-if="dish.status === 'waiting'"
                        class="table-action-btn start-btn"
                        title="开始制作"
                        :disabled="stove.stoveStatus !== 0"
                        @click="startCooking(stove.id, index)"
                      >
                        <i class="fas fa-play"></i>
                      </button>
                      <button
                        v-if="dish.status === 'cooking'"
                        class="table-action-btn pause-btn"
                        title="暂停制作"
                        :disabled="stove.stoveStatus !== 0"
                        @click="pauseCooking(stove.id, index)"
                      >
                        <i class="fas fa-pause"></i>
                      </button>
                      <button
                        v-if="dish.status === 'cooking'"
                        class="table-action-btn complete-btn"
                        title="完成"
                        :disabled="stove.stoveStatus !== 0"
                        @click="completeCooking(stove.id, index)"
                      >
                        <i class="fas fa-check"></i>
                      </button>
                      <button
                        v-if="dish.status === 'paused'"
                        class="table-action-btn resume-btn"
                        title="恢复制作"
                        :disabled="stove.stoveStatus !== 0"
                        @click="resumeCooking(stove.id, index)"
                      >
                        <i class="fas fa-play"></i>
                      </button>
                      <button
                        class="table-action-btn remove-btn"
                        title="移除"
                        @click="removeFromQueue(stove.id, index)"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 待分配菜品区域 -->
        <div class="pending-section">
          <div class="section-title">
            <i class="fas fa-utensils"></i>
            待分配菜品
            <span class="section-count"
              >({{ pendingItemCount }}道菜品，{{ pendingPortionCount }}份)</span
            >
          </div>
          <table class="pending-table">
            <thead>
              <tr>
                <th>菜品名称</th>
                <th>桌位</th>
                <th style="text-align: center">份数</th>
                <th style="text-align: center">时间</th>
                <th style="text-align: center">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="dish in filteredPendingDishes"
                :key="dish.key"
                :class="{ 'urgent-row': dish.urgent }"
              >
                <td class="dish-name-cell">
                  <div class="dish-name-spec-wrap">
                    <div class="dish-name-main">{{ dish.name }}</div>
                    <div
                      v-if="dish.foodSpecName && dish.foodSpecName !== '标准'"
                      class="dish-spec-plain"
                      >{{ dish.foodSpecName }}</div
                    >
                    <span v-if="dish.urgent" class="urgent-badge">加急</span>
                  </div>
                </td>
                <td class="dish-table-cell">{{ dish.table }}</td>
                <td class="dish-quantity-cell">{{ dish.quantity }}份</td>
                <td class="dish-time-cell">{{ dish.time }}</td>
                <td class="action-cell">
                  <button
                    v-if="!isAllKitchensMode"
                    class="assign-btn"
                    @click="showStoveSelector(dish)"
                    >分配</button
                  >
                  <div v-else class="text-gray-500 text-sm"></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 灶台选择浮层 -->
    <div v-if="showAssignModal" class="stove-selector-overlay">
      <div class="stove-selector-modal">
        <div class="modal-header">
          <h3>选择分配灶台</h3>
          <button class="close-btn" @click="hideStoveSelector">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="dish-info-display">
            <div class="dish-basic-info">
              <span class="dish-name">{{ selectedDish?.name }}</span> -
              <span class="dish-table">{{ selectedDish?.table }}</span>
            </div>
            <div class="quantity-edit-section">
              <span class="quantity-label">分配数量：</span>
              <div class="quantity-controls">
                <!-- 暂时不允许修改份数，如需开放只需去掉disabled属性 -->
                <button class="quantity-btn minus" :disabled="true" @click="adjustModalQuantity(-1)"
                  >-</button
                >
                <input
                  v-model="modalQuantity"
                  type="number"
                  class="quantity-input"
                  :max="selectedDish?.quantity"
                  min="1"
                  disabled
                />
                <button class="quantity-btn plus" :disabled="true" @click="adjustModalQuantity(1)"
                  >+</button
                >
              </div>
              <span class="quantity-max-info">/ 共{{ selectedDish?.quantity }}份</span>
            </div>
          </div>
          <div class="stove-options">
            <div
              v-for="stove in stoves"
              :key="stove.id"
              class="stove-option-card"
              :class="{ disabled: stove.stoveStatus !== 0 }"
              @click="assignDishToStove(stove.id)"
            >
              <div :class="`stove-option-icon stove-${stove.id}`">
                <i class="fas fa-fire"></i>
              </div>
              <div class="stove-option-info">
                <div class="stove-option-name">{{ stove.name }}</div>
                <div class="stove-option-status">
                  {{
                    stove.stoveStatus !== 0
                      ? '已停止，不能分配'
                      : `${getStoveStatusText(stove)} ${stove.dishes.length} 道菜`
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { useRestaurantKitchen } from '@/composables/useRestaurantKitchen';
  import { getOrderOutList, distributeStove } from '@/service/order';
  import { fetchStoveList, fetchConfiguredStoves, changeKitchenStatus } from '@/service/kitchen';

  import { fetchRestaurantKitchenFoods } from '@/service/restaurantKitchen';

  const router = useRouter();

  // 响应式数据
  const currentSort = ref('time');
  const currentFilter = ref('all');
  const showFilterMenu = ref(false);
  const showNavMenu = ref(false);
  const showAssignModal = ref(false);
  const selectedDish = ref(null);
  const modalQuantity = ref(1);

  const {
    shops,
    currentShop,
    kitchens,
    currentKitchen,
    selectionVersion,
    selectionLoading,
    selectionMessage,
    loadShops,
  } = useRestaurantKitchen('kitchen_dispatch_selected_kitchen');
  // 订单数据（从API获取）
  const pendingDishes = ref([]);

  // 灶台数据（从API获取）
  const stoves = ref([]);

  // 菜品汇总数据
  const foodSummary = ref({
    foodCount: 0,
    foodSum: 0,
  });

  // 计算属性
  const filteredPendingDishes = computed(() => {
    let filtered = pendingDishes.value.filter((dish) => {
      // 状态过滤
      if (currentFilter.value === 'urgent' && !dish.urgent) {
        return false;
      }
      return true;
    });

    // 排序
    filtered.sort((a, b) => {
      switch (currentSort.value) {
        case 'time': {
          // 加急订单优先
          if (a.urgent && !b.urgent) return -1;
          if (!a.urgent && b.urgent) return 1;
          return a.time.localeCompare(b.time);
        }
        case 'dish': {
          return a.name.localeCompare(b.name, 'zh-CN');
        }
        case 'table': {
          return a.table.localeCompare(b.table, 'zh-CN');
        }
        case 'type': {
          return a.typeCode - b.typeCode;
        }
        default:
          return 0;
      }
    });

    return filtered;
  });

  const totalDishes = computed(() => {
    return foodSummary.value.foodCount;
  });

  const totalPortions = computed(() => {
    return foodSummary.value.foodSum;
  });

  const pendingItemCount = computed(() => {
    return pendingDishes.value.length;
  });

  const pendingPortionCount = computed(() => {
    return pendingDishes.value.reduce((total, dish) => total + dish.quantity, 0);
  });

  const stoveItemCount = computed(() => {
    return stoves.value.reduce((total, stove) => total + stove.dishes.length, 0);
  });

  const stovePortionCount = computed(() => {
    return stoves.value.reduce(
      (total, stove) =>
        total + stove.dishes.reduce((stoveTotal, dish) => stoveTotal + dish.quantity, 0),
      0,
    );
  });

  // 判断是否为全部厨房模式
  const isAllKitchensMode = computed(() => {
    return currentKitchen.value === 'all';
  });

  // 方法
  const toggleFilterMenu = () => {
    showFilterMenu.value = !showFilterMenu.value;
  };

  const toggleNavMenu = () => {
    showNavMenu.value = !showNavMenu.value;
  };

  // 导航跳转方法
  const goToDishAssignment = () => {
    router.push('/dishAssignment');
    showNavMenu.value = false;
  };

  const goToKitchenOrder = () => {
    router.push('/kitchenOrder');
    showNavMenu.value = false;
  };

  const goToServingHistory = () => {
    router.push('/servingHistory');
    showNavMenu.value = false;
  };

  const goToOrderHistory = () => {
    router.push('/orderHistory');
    showNavMenu.value = false;
  };

  const goToTableManage = () => {
    router.push('/tableManage');
    showNavMenu.value = false;
  };

  const goToFoodManage = () => {
    router.push('/foodManage');
    showNavMenu.value = false;
  };

  const applyFilter = (type, value) => {
    if (type === 'sort') {
      currentSort.value = value;
    } else if (type === 'filter') {
      currentFilter.value = value;
    }
    showFilterMenu.value = false;
  };

  const refreshData = () => {
    loadPendingDishes();
    loadFoodSummary();
    if (currentKitchen.value !== 'all') loadStoves();
    showToast('数据已刷新');
  };

  const showStoveSelector = (dish) => {
    selectedDish.value = dish;
    modalQuantity.value = dish.quantity;
    showAssignModal.value = true;
  };

  const hideStoveSelector = () => {
    showAssignModal.value = false;
    selectedDish.value = null;
    modalQuantity.value = 1;
  };

  const adjustModalQuantity = (change) => {
    const newValue = modalQuantity.value + change;
    const maxValue = selectedDish.value?.quantity || 1;
    if (newValue >= 1 && newValue <= maxValue) {
      modalQuantity.value = newValue;
    }
  };

  const assignDishToStove = async (stoveId) => {
    if (!selectedDish.value) return;

    const version = selectionVersion.value;
    const stove = stoves.value.find((s) => s.id === stoveId);
    if (stove?.stoveStatus !== 0) {
      showToast('该厨师已停止接单，请先在厨师大屏启用');
      return;
    }

    try {
      const configResponse = await fetchConfiguredStoves(Number(currentKitchen.value));
      if (version !== selectionVersion.value) return;
      if (configResponse.code !== 200) throw new Error(configResponse.msg || '读取厨师状态失败');
      if (
        !configResponse.result.list.some(
          (item) => item.stoveId === stoveId && item.stoveStatus === 0,
        )
      ) {
        showToast('该厨师已停止接单，请选择其他厨师');
        await loadStoves();
        return;
      }
      // 调用分配灶台API
      const response = await distributeStove({
        orderOutId: selectedDish.value.orderOutId,
        stoveId: stoveId,
      });

      if (version !== selectionVersion.value) return;
      if (response.code === 200) {
        const pendingResponse = await changeKitchenStatus({
          orderOutId: selectedDish.value.orderOutId,
          kitchenStatus: 0,
        });
        if (pendingResponse.code !== 200)
          throw new Error(pendingResponse.msg || '设置待制作状态失败');
        if (version !== selectionVersion.value) return;
        // API调用成功，更新本地状态
        const dishInfo = {
          name: selectedDish.value.name,
          quantity: modalQuantity.value,
          status: 'waiting',
          orderNumber: selectedDish.value.orderNo,
          tableNumber: selectedDish.value.table.replace(/^.*【(.*)】$/, '$1'),
          restaurantName: selectedDish.value.restaurant,
          type: selectedDish.value.type,
          orderOutId: selectedDish.value.orderOutId,
          foodSpecName: selectedDish.value.foodSpecName,
          assignTime: new Date().toLocaleTimeString(),
        };

        // 添加到灶台队列
        stove.dishes.push(dishInfo);
        updateStoveStatus(stove);

        // 处理待分配菜品列表
        const remainingQuantity = selectedDish.value.quantity - modalQuantity.value;
        if (remainingQuantity > 0) {
          // 更新剩余数量
          selectedDish.value.quantity = remainingQuantity;
        } else {
          // 全部分配完，从列表中移除
          const index = pendingDishes.value.findIndex(
            (dish) => dish.key === selectedDish.value.key,
          );
          if (index > -1) {
            pendingDishes.value.splice(index, 1);
          }
        }

        hideStoveSelector();
        showToast(`${dishInfo.name} ${modalQuantity.value}份 已分配到 ${stove.name}`);

        // 重新加载数据以同步最新状态
        setTimeout(() => {
          loadPendingDishes();
          loadStoves();
          loadFoodSummary();
        }, 200);
      } else {
        showToast(`分配失败: ${response.msg || '未知错误'}`);
      }
    } catch (error) {
      console.error('分配灶台失败:', error);
      showToast('分配灶台失败，请稍后重试');
      await Promise.all([loadPendingDishes(), loadStoves()]);
    }
  };

  const updateStoveStatus = (stove) => {
    if (stove.dishes.length === 0) {
      stove.status = 'idle';
    } else {
      const cookingDishes = stove.dishes.filter((dish) => dish.status === 'cooking');
      const pausedDishes = stove.dishes.filter((dish) => dish.status === 'paused');

      if (cookingDishes.length > 0) {
        stove.status = 'cooking';
      } else if (pausedDishes.length > 0) {
        stove.status = 'paused';
      } else {
        stove.status = 'busy';
      }
    }
  };

  const assertStoveEnabled = async (stoveId) => {
    const response = await fetchConfiguredStoves(Number(currentKitchen.value));
    if (response.code !== 200) throw new Error(response.msg || '读取厨师状态失败');
    if (
      !response.result.list.some((stove) => stove.stoveId === stoveId && stove.stoveStatus === 0)
    ) {
      await loadStoves();
      throw new Error('该厨师已停止接单，请刷新后重试');
    }
  };

  const startCooking = async (stoveId, dishIndex) => {
    const stove = stoves.value.find((s) => s.id === stoveId);
    if (stove && stove.dishes[dishIndex]) {
      const dish = stove.dishes[dishIndex];
      try {
        await assertStoveEnabled(stoveId);
        const response = await changeKitchenStatus({
          orderOutId: dish.orderOutId,
          kitchenStatus: 5, // 制作中
        });

        if (response.code === 200) {
          stove.dishes[dishIndex].status = 'cooking';
          stove.dishes[dishIndex].startTime = new Date().toLocaleTimeString();
          updateStoveStatus(stove);
          showToast(`开始制作 ${dish.name}`);
        } else {
          showToast(`开始制作失败: ${response.msg || '未知错误'}`);
        }
      } catch (error) {
        console.error('开始制作失败:', error);
        showToast('开始制作失败，请稍后重试');
      }
    }
  };

  const pauseCooking = async (stoveId, dishIndex) => {
    const stove = stoves.value.find((s) => s.id === stoveId);
    if (stove && stove.dishes[dishIndex]) {
      const dish = stove.dishes[dishIndex];
      try {
        await assertStoveEnabled(stoveId);
        const response = await changeKitchenStatus({
          orderOutId: dish.orderOutId,
          kitchenStatus: 9, // 已暂停
        });

        if (response.code === 200) {
          stove.dishes[dishIndex].status = 'paused';
          updateStoveStatus(stove);
          showToast(`已暂停制作 ${dish.name}`);
        } else {
          showToast(`暂停制作失败: ${response.msg || '未知错误'}`);
        }
      } catch (error) {
        console.error('暂停制作失败:', error);
        showToast('暂停制作失败，请稍后重试');
      }
    }
  };

  const resumeCooking = async (stoveId, dishIndex) => {
    const stove = stoves.value.find((s) => s.id === stoveId);
    if (stove && stove.dishes[dishIndex]) {
      const dish = stove.dishes[dishIndex];
      try {
        await assertStoveEnabled(stoveId);
        const response = await changeKitchenStatus({
          orderOutId: dish.orderOutId,
          kitchenStatus: 5, // 制作中
        });

        if (response.code === 200) {
          stove.dishes[dishIndex].status = 'cooking';
          updateStoveStatus(stove);
          showToast(`恢复制作 ${dish.name}`);
        } else {
          showToast(`恢复制作失败: ${response.msg || '未知错误'}`);
        }
      } catch (error) {
        console.error('恢复制作失败:', error);
        showToast('恢复制作失败，请稍后重试');
      }
    }
  };

  const completeCooking = async (stoveId, dishIndex) => {
    const stove = stoves.value.find((s) => s.id === stoveId);
    if (stove && stove.dishes[dishIndex]) {
      const dish = stove.dishes[dishIndex];
      try {
        await assertStoveEnabled(stoveId);
        const response = await changeKitchenStatus({
          orderOutId: dish.orderOutId,
          kitchenStatus: 10, // 已完成
        });

        if (response.code === 200) {
          showToast(`${dish.name} 制作完成！`);

          // 重新加载数据以同步最新状态
          setTimeout(() => {
            loadPendingDishes();
            loadStoves();
            loadFoodSummary();
          }, 200);
        } else {
          showToast(`完成制作失败: ${response.msg || '未知错误'}`);
        }
      } catch (error) {
        console.error('完成制作失败:', error);
        showToast('完成制作失败，请稍后重试');
      }
    }
  };

  const removeFromQueue = async (stoveId, dishIndex) => {
    const stove = stoves.value.find((s) => s.id === stoveId);
    if (stove && stove.dishes[dishIndex]) {
      const dish = stove.dishes[dishIndex];
      try {
        // 调用分配接口，stoveId 传 null
        const response = await distributeStove({
          orderOutId: dish.orderOutId,
          stoveId: null,
        });
        if (response.code === 200) {
          // 重新添加到待分配列表，桌号格式为“店铺名【桌号】”
          const tableStr = `${dish.restaurantName || ''}【${dish.tableNumber || ''}】`;
          const timeStr = dish.assignTime
            ? (() => {
                const d = new Date(dish.assignTime);
                if (isNaN(d.getTime())) {
                  // 兜底：如果是'12:34:56'这种格式，直接截取前5位
                  if (typeof dish.assignTime === 'string' && dish.assignTime.length >= 5) {
                    return dish.assignTime.slice(0, 5);
                  }
                  return '';
                }
                return (
                  d.getHours().toString().padStart(2, '0') +
                  ':' +
                  d.getMinutes().toString().padStart(2, '0')
                );
              })()
            : '';
          const newDish = {
            key: `${Date.now()}_${Math.random()}`,
            name: dish.name,
            table: tableStr,
            restaurant: dish.restaurantName || '',
            quantity: dish.quantity,
            time: timeStr,
            type: dish.type,
            typeCode: dish.typeCode,
            urgent: false,
            orderOutId: dish.orderOutId,
            orderNo: dish.orderNumber,
            foodSpecName: dish.foodSpecName,
          };
          pendingDishes.value.push(newDish);
          // 从灶台移除
          stove.dishes.splice(dishIndex, 1);
          updateStoveStatus(stove);
          showToast(`${dish.name} 已撤回到待分配列表`);
        } else {
          showToast(`撤回失败: ${response.msg || '未知错误'}`);
        }
      } catch (error) {
        console.error('撤回分配失败:', error);
        showToast('撤回分配失败，请稍后重试');
      }
    }
  };

  const getStoveStatusClass = (stove) => {
    if (stove.dishes.length === 0) return 'empty';

    const hasCooking = stove.dishes.some((dish) => dish.status === 'cooking');
    if (hasCooking) return 'cooking';

    return 'busy';
  };

  const getStoveStatusText = (stove) => {
    if (stove.stoveStatus !== 0) return '已停止';
    if (stove.dishes.length === 0) return '空闲';

    const cookingCount = stove.dishes.filter((dish) => dish.status === 'cooking').length;
    const waitingCount = stove.dishes.filter((dish) => dish.status === 'waiting').length;
    const pausedCount = stove.dishes.filter((dish) => dish.status === 'paused').length;

    if (cookingCount > 0) return '烹饪中';
    if (pausedCount > 0) return '已暂停';
    if (waitingCount > 0) return '等待中';

    return '队列';
  };

  const getDishStatusText = (status) => {
    const statusMap = {
      waiting: '等待中',
      cooking: '制作中',
      paused: '已暂停',
      completed: '已完成',
    };
    return statusMap[status] || '未知';
  };

  const getDishStatusColor = (status) => {
    const colorMap = {
      waiting: '#f39c12',
      cooking: '#27ae60',
      paused: '#6c757d',
      completed: '#007bff',
    };
    return colorMap[status] || '#6c757d';
  };

  const getTypeCode = (type) => {
    return type || 2; // 默认为热菜
  };

  const showToast = (message) => {
    // 创建提示框
    const toast = document.createElement('div');
    toast.className = 'toast-message';
    toast.textContent = message;
    document.body.appendChild(toast);

    // 显示动画
    setTimeout(() => {
      toast.classList.add('show');
    }, 100);

    // 自动隐藏
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 2000);
  };

  // API 加载方法
  let loadPendingDishesRequest = 0;
  const loadPendingDishes = async () => {
    if (!currentShop.value || !currentKitchen.value || !kitchens.value.length) return;
    const version = selectionVersion.value;
    const request = ++loadPendingDishesRequest;

    try {
      // 构建查询参数
      const queryData = { shopId: Number(currentShop.value) };
      // 只有当不是"全部"时才传kitchenId参数
      if (currentKitchen.value !== 'all') {
        queryData.kitchenId = currentKitchen.value;
      }

      const response = await getOrderOutList({
        pageNum: 1,
        pageSize: 999,
        data: queryData,
      });

      if (version !== selectionVersion.value || request !== loadPendingDishesRequest) return;
      if (response.code !== 200) throw new Error(response.msg || '加载失败');
      if (response.code === 200 && response.result && response.result.list) {
        // 提取所有待分配的菜品（kitchenStatus为0且未分配到灶台）
        const allDishes = [];

        response.result.list.forEach((orderItem) => {
          (orderItem.detailsList || []).forEach((detail) => {
            // 只显示待分配状态的菜品（kitchenStatus === 0 且没有分配到灶台）
            if (
              Number(detail.shopId ?? orderItem.shopId) === Number(currentShop.value) &&
              kitchens.value.some((item) => Number(item.id) === Number(detail.kitchenId)) &&
              (currentKitchen.value === 'all' ||
                Number(detail.kitchenId) === Number(currentKitchen.value)) &&
              detail.kitchenStatus === 0 &&
              !detail.stoveId
            ) {
              const timeStr = detail.createTime
                ? new Date(detail.createTime).toLocaleTimeString('zh-CN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '';

              allDishes.push({
                key: `${detail.orderOutId}_${detail.orderFoodId}`,
                name: detail.shopFoodName || detail.foodName || '',
                table: `${orderItem.shopName || ''}【${orderItem.tableName || ''}】`,
                restaurant: orderItem.shopName || '',
                quantity: detail.quantity || 1,
                time: timeStr,
                type: detail.foodType || 2,
                typeCode: detail.foodType || 2,
                urgent: detail.operateStatus === 10,
                orderOutId: detail.orderOutId,
                orderNo: orderItem.orderNo || orderItem.orderId?.toString() || '',
                foodSpecName: detail.foodSpecName || detail.shopFoodSpecName || '',
              });
            }
          });
        });

        // 按菜品名称和桌位排序
        allDishes.sort((a, b) => {
          // 先按菜品名称排序
          const nameCompare = a.name.localeCompare(b.name, 'zh-CN');
          if (nameCompare !== 0) return nameCompare;

          // 再按桌位排序
          return a.table.localeCompare(b.table, 'zh-CN');
        });

        pendingDishes.value = allDishes;
        console.log('待分配菜品加载成功:', pendingDishes.value);
      }
    } catch (error) {
      if (version !== selectionVersion.value || request !== loadPendingDishesRequest) return;
      showToast('加载菜品失败，请重试');
      console.error('待分配菜品加载异常:', error);
      pendingDishes.value = [];
    }
  };

  let loadStovesRequest = 0;
  const loadStoves = async () => {
    if (currentKitchen.value === 'all') return;
    if (!currentShop.value || !currentKitchen.value || !kitchens.value.length) return;
    const version = selectionVersion.value;
    const request = ++loadStovesRequest;

    try {
      // 构建查询参数
      const queryData = {};
      // 只有当不是"全部"时才传kitchenId参数
      if (currentKitchen.value !== 'all') {
        queryData.kitchenId = currentKitchen.value;
      }

      const [response, configResponse] = await Promise.all([
        fetchStoveList({
          pageNum: 1,
          pageSize: 999,
          data: queryData,
        }),
        fetchConfiguredStoves(Number(currentKitchen.value)),
      ]);

      if (version !== selectionVersion.value || request !== loadStovesRequest) return;
      if (response.code !== 200) throw new Error(response.msg || '加载失败');
      if (configResponse.code !== 200) throw new Error(configResponse.msg || '加载厨师状态失败');
      if (response.code === 200 && response.result && response.result.list) {
        stoves.value = (configResponse.result.list || []).map((config) => {
          const stoveItem = response.result.list.find((item) => item.stoveId === config.stoveId);
          // 过滤掉已完成的菜品（kitchenStatus === 10）
          const activeDishes = (stoveItem?.detailsList || [])
            .filter(
              (detail) =>
                Number(detail.shopId) === Number(currentShop.value) &&
                detail.kitchenStatus !== 10 &&
                ![8, 9, 999].includes(detail.status),
            )
            .map((detail) => ({
              name: detail.shopFoodName || detail.foodName || '',
              quantity: detail.quantity || 1,
              status: mapKitchenStatusToDishStatus(detail.kitchenStatus),
              orderNumber: detail.orderNo || detail.orderId?.toString() || '',
              tableNumber: detail.tableName || '未知桌号',
              restaurantName: detail.shopName || '未知餐厅',
              type: detail.foodType || 2,
              orderOutId: detail.orderOutId,
              assignTime: detail.createTime ? new Date(detail.createTime).toLocaleTimeString() : '',
              foodSpecName: detail.foodSpecName || detail.shopFoodSpecName || '',
              foodCookDesc: detail.foodCookDesc || '', // 添加做法描述
            }));

          return {
            id: config.stoveId,
            name: config.stoveName || `灶台${config.stoveId}`,
            stoveStatus: config.stoveStatus,
            status: 'idle',
            dishes: activeDishes,
          };
        });

        // 更新所有灶台状态
        stoves.value.forEach((stove) => updateStoveStatus(stove));

        console.log('灶台列表加载成功:', stoves.value);
      }
    } catch (error) {
      if (version !== selectionVersion.value || request !== loadStovesRequest) return;
      showToast('加载菜品失败，请重试');
      console.error('灶台列表加载异常:', error);
      stoves.value = [];
    }
  };

  const mapKitchenStatusToDishStatus = (kitchenStatus) => {
    switch (kitchenStatus) {
      case 0:
        return 'waiting'; // 待分配
      case 5:
        return 'cooking'; // 开始时间仅用于计时，制作状态以服务端为准
      case 9:
        return 'paused'; // 已暂停
      case 10:
        return 'completed'; // 已完成
      default:
        return 'waiting';
    }
  };

  let summaryRequest = 0;
  const loadFoodSummary = async () => {
    if (!currentShop.value || !currentKitchen.value || !kitchens.value.length) return;
    const version = selectionVersion.value;
    const request = ++summaryRequest;
    try {
      const kitchenIds =
        currentKitchen.value === 'all'
          ? kitchens.value.map((item) => Number(item.id))
          : [Number(currentKitchen.value)];
      const details = await fetchRestaurantKitchenFoods(Number(currentShop.value), kitchenIds);
      if (version !== selectionVersion.value || request !== summaryRequest) return;
      foodSummary.value = {
        foodCount: new Set(details.map((item) => item.foodId)).size,
        foodSum: details.reduce((total, item) => total + Number(item.quantity || 0), 0),
      };
    } catch (error) {
      if (version !== selectionVersion.value || request !== summaryRequest) return;
      console.error('加载菜品汇总失败', error);
      showToast('加载菜品汇总失败，请重试');
      foodSummary.value = { foodCount: 0, foodSum: 0 };
    }
  };

  const formatToHourMinute = (dateOrStr) => {
    if (!dateOrStr) return '';
    const d = typeof dateOrStr === 'string' ? new Date(dateOrStr) : dateOrStr;
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    return (
      d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0')
    );
  };

  // 生命周期钩子
  onMounted(() => {
    loadShops();

    // 点击外部关闭筛选菜单
    const handleClickOutside = (e) => {
      const filterDropdown = document.querySelector('.filter-dropdown');
      const navDropdown = document.querySelector('.nav-dropdown');
      if (filterDropdown && !filterDropdown.contains(e.target)) {
        showFilterMenu.value = false;
      }
      if (navDropdown && !navDropdown.contains(e.target)) {
        showNavMenu.value = false;
      }
    };

    document.addEventListener('click', handleClickOutside);

    // ESC键关闭弹窗
    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        hideStoveSelector();
      }
    };

    document.addEventListener('keydown', handleKeydown);

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    });
  });

  watch(
    selectionVersion,
    () => {
      pendingDishes.value = [];
      stoves.value = [];
      foodSummary.value = { foodCount: 0, foodSum: 0 };
      showAssignModal.value = false;
      selectedDish.value = null;
      modalQuantity.value = 1;
    },
    { flush: 'sync' },
  );

  watch(currentKitchen, (newVal) => {
    if (newVal) {
      loadPendingDishes();
      loadFoodSummary();
      if (newVal !== 'all') loadStoves();
    }
  });
</script>

<style scoped>
  .header-info,
  .restaurant-selector,
  .title-section {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  /* 引入原有的样式，但进行Vue适配 */
  .kitchen-bg {
    background-color: #f2f2f7;
    padding: 10px;
    color: #000000;
    background-image: url('@/assets/bg.jpg');
    background-size: 100% 100%;
    font-size: 18px;
    min-height: 100vh;
  }

  .kitchen-container {
    max-width: 100%;
    margin: 0 auto;
    padding: 0 4px;
  }

  .fixed-header {
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  }

  .content-container {
    /* padding-top: 120px; */
  }

  .fixed-header .kitchen-container {
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 35px;
    padding-right: 35px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
  }

  .title-section {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  .title {
    font-size: 40px;
    font-weight: 700;
    color: #ffffff;
  }

  .restaurant-selector {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: 100%;
  }

  .restaurant-dropdown {
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    color: #ffffff;
    font-size: 20px;
    font-weight: 700;
    padding: 12px 60px 12px 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    min-width: 200px;
    appearance: auto;
    max-width: 100%;
  }

  .restaurant-dropdown:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  .restaurant-dropdown option {
    background-color: #1c1c1e;
    color: #ffffff;
    padding: 12px;
    font-size: 18px;
    font-weight: 500;
  }

  .summary-info {
    display: flex;
    align-items: center;
  }

  .summary-badge {
    background: linear-gradient(135deg, #ff3b30, #ff6b6b);
    color: white;
    padding: 12px 14px;
    border-radius: 20px;
    font-size: 18px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(255, 59, 48, 0.3);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  /* .summary-badge.completed {
    background: linear-gradient(135deg, #34c759, #30d158);
    box-shadow: 0 2px 8px rgba(52, 199, 89, 0.3);
  } */

  .actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .nav-btn {
    background: linear-gradient(135deg, #007aff, #0051d5);
    color: white;
    border: none;
    padding: 12px 14px;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
  }

  .nav-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
  }

  .filter-dropdown {
    position: relative;
    display: inline-block;
  }

  .filter-btn {
    background-color: rgba(255, 255, 255, 0.1);
    color: rgb(255, 255, 255);
    border: 2px solid rgba(255, 254, 254, 0.2);
    padding: 8px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }

  .filter-btn:hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .filter-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border: 1px solid #e0e0e0;
    min-width: 200px;
    z-index: 1001;
    padding: 8px 0;
    margin-top: 8px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.2s ease;
  }

  .filter-menu.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .filter-menu-section {
    padding: 12px 16px 8px;
    border-bottom: 1px solid #e0e0e0;
  }

  .filter-menu-section:last-child {
    border-bottom: none;
  }

  .filter-menu-title {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  .filter-menu-item {
    display: block;
    width: 100%;
    padding: 8px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    border-radius: 8px;
    margin: 2px 0;
    transition: all 0.2s;
  }

  .filter-menu-item:hover {
    background-color: rgba(0, 122, 255, 0.1);
    color: #007aff;
  }

  .filter-menu-item.active {
    background-color: #007aff;
    color: white;
  }

  /* 新增：导航菜单样式 */
  .nav-dropdown {
    position: relative;
    display: inline-block;
  }

  .nav-menu-btn {
    background: #007aff;
    color: white;
    border: none;
    padding: 12px 16px;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
  }

  .nav-menu-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
  }

  .nav-menu-btn:active {
    transform: translateY(0);
  }

  .nav-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e5ea;
    min-width: 200px;
    z-index: 1000;
    padding: 8px 0;
    margin-top: 8px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.2s ease;
  }

  .nav-menu.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .nav-menu-item {
    display: block;
    width: 100%;
    padding: 12px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 16px;
    color: #000000;
    cursor: pointer;
    border-radius: 8px;
    margin: 2px 8px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .nav-menu-item:hover {
    background-color: rgba(0, 122, 255, 0.1);
    color: #007aff;
  }

  .nav-menu-item:active {
    transform: scale(0.98);
  }

  .nav-menu-item i {
    width: 16px;
    font-size: 14px;
  }

  .ios-button-secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.2);
    padding: 8px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }

  .ios-button-secondary:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .dish-layout {
    display: flex;
    flex-direction: row;
    gap: 24px;
    /* height: calc(100vh - 140px); */
  }

  .stove-section {
    border-radius: 16px;
    padding: 10px;
    flex: 1;
    min-height: 300px;
  }

  .pending-section {
    border-radius: 16px;
    padding: 20px;
    flex: 1;
    min-height: 300px;
  }

  .section-title {
    font-size: 30px;
    font-weight: 600;
    margin-bottom: 20px;
    color: white;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .section-count {
    font-size: 18px;
    font-weight: 500;
    color: #8e8e93;
    background-color: rgba(255, 255, 255, 0.1);
    padding: 4px 8px;
    border-radius: 8px;
  }

  .stove-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 16px;
  }

  .stove-table-container {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #f0f0f0;
    overflow: hidden;
    transition: all 0.2s ease;
  }

  .stove-table-container.empty {
    border-color: #dee2e6;
    background-color: #f8f9fa;
  }

  .stove-table-container.busy {
    border-color: #f39c12;
    background-color: #fff3cd;
    box-shadow: 0 4px 15px rgba(243, 156, 18, 0.15);
  }

  .stove-table-container.cooking {
    border-color: #27ae60;
    background-color: #d1f2eb;
    box-shadow: 0 4px 15px rgba(39, 174, 96, 0.15);
    animation: cooking-glow 3s ease-in-out infinite alternate;
  }

  @keyframes cooking-glow {
    0% {
      box-shadow: 0 4px 15px rgba(39, 174, 96, 0.15);
    }
    100% {
      box-shadow: 0 6px 20px rgba(39, 174, 96, 0.25);
    }
  }

  .stove-table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 4px;
    background-color: rgba(0, 0, 0, 0.02);
    border-bottom: 1px solid #e5e5ea;
  }

  .stove-title {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  .stove-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: white;
    position: relative;
  }

  .stove-1 .stove-icon {
    background-color: #ff6b6b;
  }
  .stove-2 .stove-icon {
    background-color: #4ecdc4;
  }
  .stove-3 .stove-icon {
    background-color: #099cbd;
  }
  .stove-4 .stove-icon {
    background-color: #017d43;
  }
  .stove-5 .stove-icon {
    background-color: #f4c423;
  }
  .stove-6 .stove-icon {
    background-color: #dda0dd;
  }

  .stove-name {
    font-size: 22px;
    font-weight: 600;
    color: #000000;
    margin: 0;
  }

  .stove-status-badge {
    font-size: 16px;
    font-weight: 500;
    padding: 5px 6px;
    border-radius: 16px;
    background-color: rgba(255, 255, 255, 0.8);
    color: #000000;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .stove-table {
    width: 100%;
    border-collapse: collapse;
    background-color: white;
  }

  .stove-table th {
    background-color: rgba(0, 0, 0, 0.03);
    color: #000000;
    font-weight: 600;
    padding: 4px 8px;
    text-align: left;
    border-bottom: 1px solid #e5e5ea;
    font-size: 16px;
    white-space: nowrap;
  }

  .stove-table td {
    padding: 6px 5px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    color: #000000;
    vertical-align: middle;
    font-size: 17px;
  }

  .stove-table tbody tr:hover {
    background-color: rgba(0, 122, 255, 0.04);
  }

  .stove-table tbody tr.cooking {
    background-color: rgba(40, 167, 69, 0.08);
  }

  .stove-table tbody tr.waiting {
    background-color: rgba(255, 193, 7, 0.08);
  }

  .stove-table tbody tr.paused {
    background-color: rgba(108, 117, 125, 0.08);
  }

  .empty-stove-row {
    text-align: center;
    padding: 40px 20px;
    color: #8e8e93;
    font-size: 17px;
    opacity: 0.6;
  }

  .empty-stove-row i {
    font-size: 28px;
    margin-bottom: 12px;
    display: block;
    opacity: 0.5;
  }

  .quantity-badge {
    background: linear-gradient(135deg, #6c5ce7, #a29bfe);
    color: white;
    font-size: 15px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 16px;
    min-width: 40px;
    text-align: center;
    display: inline-block;
  }

  .table-action-btn {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    margin: 0 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }

  .table-action-btn:hover {
    transform: translateY(-1px);
  }

  .table-action-btn.start-btn {
    background-color: #28a745;
    color: white;
  }

  .table-action-btn.complete-btn {
    background-color: #007bff;
    color: white;
  }

  .table-action-btn.remove-btn {
    background-color: #dc3545;
    color: white;
  }

  .table-action-btn.pause-btn {
    background-color: #ffc107;
    color: white;
  }

  .table-action-btn.resume-btn {
    background-color: #17a2b8;
    color: white;
  }

  .pending-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
    background-color: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e5ea;
  }

  .pending-table th {
    background-color: #f2f2f7;
    color: #000000;
    font-weight: 600;
    padding: 6px 2px;
    text-align: left;
    border-bottom: 1px solid #e5e5ea;
    font-size: 18px;
  }

  .pending-table td {
    padding: 4px 2px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    color: #000000;
    vertical-align: middle;
    font-size: 17px;
  }

  .pending-table tbody tr:hover {
    background-color: rgba(0, 122, 255, 0.05);
  }

  .urgent-row {
    background-color: rgba(255, 59, 48, 0.05);
    border-left: 4px solid #ff3b30;
  }

  .dish-name-cell {
    font-weight: 600;
    color: #000000;
    font-size: 18px;
    text-align: left;
  }

  .dish-table-cell {
    color: #8e8e93;
    font-size: 16px;
    text-align: left;
  }

  .dish-quantity-cell {
    text-align: center;
    font-weight: 600;
    color: #ff3b30;
    font-size: 18px;
  }

  .dish-time-cell {
    color: #8e8e93;
    font-size: 16px;
  }

  .action-cell {
    text-align: center;
    white-space: nowrap;
  }

  .assign-btn {
    background-color: #007aff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 80px;
    height: 44px;
  }

  .assign-btn:hover {
    background-color: #0056cc;
    transform: translateY(-1px);
  }

  .urgent-badge {
    background-color: #ff3b30;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    margin-left: 8px;
  }

  .dish-type-badge {
    font-size: 13px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: 12px;
    margin-right: 8px;
    color: white;
  }

  .dish-type-badge.cold {
    background-color: #5ac8fa;
  }

  .dish-type-badge.hot {
    background-color: #ff9500;
  }

  .dish-type-badge.dim-sum {
    background-color: #34c759;
  }

  .dish-type-badge.dessert {
    background-color: #af52de;
  }

  .dish-type-badge.drink {
    background-color: #007aff;
  }

  .dish-type-badge.soup {
    background-color: #ff6b6b;
  }

  /* 灶台选择浮层 */
  .stove-selector-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1002;
    backdrop-filter: blur(4px);
  }

  .stove-selector-modal {
    background-color: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow: hidden;
    animation: modalSlideIn 0.3s ease;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 10px;
    border-bottom: 1px solid #e5e5ea;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #000000;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #8e8e93;
    cursor: pointer;
    padding: 12px;
    border-radius: 12px;
    transition: all 0.2s;
    min-width: 38px;
    min-height: 38px;
  }

  .close-btn:hover {
    background-color: #f2f2f7;
    color: #000000;
  }

  .modal-body {
    padding: 5px;
  }

  .dish-info-display {
    background-color: #f2f2f7;
    padding: 5px;
    border-radius: 5px;
    margin-bottom: 5px;
    font-size: 20px;
    font-weight: 500;
    color: #000000;
  }

  .dish-basic-info {
    text-align: center;
    margin-bottom: 5px;
    font-size: 20px;
  }

  .quantity-edit-section {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .quantity-label {
    font-size: 18px;
    color: #8e8e93;
    white-space: nowrap;
  }

  .quantity-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: white;
    border-radius: 12px;
    padding: 8px;
    border: 1px solid #e5e5ea;
  }

  .quantity-btn {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 10px;
    background-color: #007aff;
    color: white;
    font-size: 20px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }

  .quantity-btn:hover {
    background-color: #0056cc;
    transform: scale(1.05);
  }

  .quantity-btn:disabled {
    background-color: #8e8e93;
    cursor: not-allowed;
    transform: none;
  }

  .quantity-input {
    width: 80px;
    height: 44px;
    border: none;
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    color: #000000;
    background-color: transparent;
    outline: none;
  }

  .quantity-max-info {
    font-size: 18px;
    color: #8e8e93;
    white-space: nowrap;
  }

  .stove-options {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stove-option-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    border: 2px solid #e5e5ea;
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.2s;
    background-color: white;
    min-height: 70px;
  }

  .stove-option-card:hover {
    border-color: #007aff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.1);
  }

  .stove-option-card.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #8e8e93;
  }

  .stove-option-card.disabled:hover {
    transform: none;
    box-shadow: none;
    border-color: #8e8e93;
  }

  .stove-option-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    color: white;
  }

  .stove-option-info {
    flex: 1;
  }

  .stove-option-name {
    font-size: 20px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 6px;
  }

  .stove-option-status {
    font-size: 17px;
    color: #8e8e93;
  }

  /* Toast 消息样式 */
  .toast-message {
    position: fixed;
    top: 20px;
    right: 20px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 16px;
    z-index: 9999;
    transform: translateX(100%);
    transition: transform 0.3s ease;
  }

  .toast-message.show {
    transform: translateX(0);
  }

  /* 响应式设计 */
  @media (max-width: 1024px) {
    .dish-layout {
      flex-direction: column;
    }

    .stove-section {
      flex: 1;
    }

    .pending-section {
      flex: 1;
    }

    .stove-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .kitchen-container {
      padding: 15px;
    }

    .content-container {
      padding-top: 15px;
    }

    .header {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .title-section {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .actions {
      width: 100%;
      justify-content: flex-start;
    }

    .stove-options {
      grid-template-columns: 1fr;
    }

    .stove-selector-modal {
      width: 95%;
      margin: 20px;
    }
  }

  .dish-name-spec-wrap {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }
  .dish-name-main {
    font-weight: 600;
    font-size: 18px;
    color: #000;
    line-height: 1.2;
  }
  .dish-spec-plain {
    margin-left: 8px;
  }
  @media (max-width: 1280px) {
    .dish-name-spec-wrap {
      flex-direction: column;
      align-items: flex-start;
    }
    .dish-name-main {
      font-size: 16px;
    }
    .dish-spec-plain {
      margin-left: 0;
      font-size: 13px;
    }
  }
</style>
